import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useSavedSearchSelect, type SavedSearch } from "common/hooks";
import { useSetAtom } from "jotai";
import { activeSavedSearchIdAtom } from "@/state/atoms";

export interface UseHandleSavedSearchSelectOptions {
  /** Called after successful search selection */
  onSuccess?: () => void;
  /** Navigate to search page after success (default: true) */
  navigateToSearch?: boolean;
}

/**
 * Centralized hook for handling saved search selection with optional post-success actions
 * 
 * This hook wraps useSavedSearchSelect and provides a consistent way to handle
 * saved search selection across the application, with optional callbacks for
 * closing overlays and navigation.
 * 
 * @param options - Configuration options
 * @param options.onSuccess - Callback executed after successful search selection (e.g., close overlay)
 * @param options.navigateToSearch - Whether to navigate to /search after success (default: true)
 * @returns A memoized handler function for saved search selection
 * 
 * @example
 * // Simple usage with navigation
 * const handleSavedSearchSelect = useHandleSavedSearchSelect();
 * 
 * @example
 * // With overlay close callback, no navigation
 * const handleSavedSearchSelect = useHandleSavedSearchSelect({
 *   onSuccess: () => setIsMobileSearchOpen(false),
 *   navigateToSearch: false,
 * });
 * 
 * @example
 * // With overlay close callback and navigation
 * const handleSavedSearchSelect = useHandleSavedSearchSelect({
 *   onSuccess: () => setIsSearchOverlayOpen(false),
 * });
 */
export function useHandleSavedSearchSelect(
  options: UseHandleSavedSearchSelectOptions = {}
) {
  const { onSuccess, navigateToSearch = true } = options;
  const navigate = useNavigate();
  const { handleSelect: selectSavedSearch } = useSavedSearchSelect();
  const setActiveSavedSearchId = useSetAtom(activeSavedSearchIdAtom);

  const handleSavedSearchSelect = useCallback(
    async (search: SavedSearch) => {
      const success = await selectSavedSearch(search);
      if (success) {
        // Set the active saved search ID to track that we're viewing saved search results
        setActiveSavedSearchId(search.id);
        
        // Execute success callback if provided (e.g., close overlay)
        onSuccess?.();
        
        // Navigate to search page if enabled
        if (navigateToSearch) {
          navigate({ to: "/search" });
        }
      }
    },
    [selectSavedSearch, onSuccess, navigateToSearch, navigate, setActiveSavedSearchId]
  );

  return handleSavedSearchSelect;
}
